import type {
  Actividad,
  ActualizarDensidadRequest,
  ActualizarEnsayoEsclerometroRequest,
  ActualizarEnsayoRequest,
  ActualizarM3ColadaManualRequest,
  ActualizarMedicionRequest,
  ActualizarMuestraRequest,
  ActualizarReboteRequest,
  ActualizarUmbralRequest,
  AnularColadaRequest,
  AnularColadasBulkRequest,
  AnularColadasBulkResponse,
  AsignarObraRequest,
  AsignarRolRequest,
  BatchHuerfano,
  CambiarActivoOperadorFioriRequest,
  ColadaDetalle,
  ColadaListadoItem,
  ConfigurarPinLabRequest,
  ConsolidarColadasRequest,
  CrearActividadRequest,
  CrearColadaManualRequest,
  CrearDensidadRequest,
  CrearEnsayoEsclerometroRequest,
  CrearEnsayoRequest,
  CrearMedicionRequest,
  CrearMuestraRequest,
  CrearOperadorFioriRequest,
  CrearPedidoEnsambladoBCRequest,
  CrearReboteRequest,
  DensidadMaterial,
  Ensayo,
  EnsayoEsclerometroDetalle,
  EstadoPinLabResponse,
  EstadoSolicitudPedidoBC,
  ExcluirBatchRequest,
  FotoMuestra,
  GuardarBomOverrideRequest,
  GuardarMapeoRecetaRequest,
  HistorialImportacionesRequest,
  HistorialImportacionesResponse,
  ImportarExcelLabResponse,
  IngestaBlendRequest,
  IngestaBlendResponse,
  KpisRequest,
  KpisResponse,
  ListaBatchesResponse,
  ListaBomBCResponse,
  ListaDensidadesResponse,
  ListaEnsayosEsclerometroResponse,
  ListaFotosMuestraResponse,
  ListaMuestrasResponse,
  ListaOperadoresFioriAdminResponse,
  ListaOperadoresFioriPublicosResponse,
  ListaPlantasResponse,
  ListaProductosEnsambladosBCResponse,
  ListaRecetasBlendMapeoResponse,
  ListaSolicitudesPedidoBCResponse,
  ListaUmbralesResponse,
  ListaUsuariosResponse,
  ListarBatchesRequest,
  ListarColadasRequest,
  ListarEnsayosEsclerometroRequest,
  ListarMuestrasRequest,
  ListarObrasRequest,
  ListarUsuariosRequest,
  LoginFioriPinRequest,
  LoginFioriPinResponse,
  LoginLabPinRequest,
  LoginLabPinResponse,
  MarcarDigitadaRequest,
  Medicion,
  MuestraDetalle,
  Obra,
  ObtenerBomColadaResponse,
  OperadorFioriAdmin,
  PreviewPedidoEnsambladoBC,
  PuntoCurvaTeorica,
  Rebote,
  RecetaBlendMapeo,
  RechazarSolicitudPedidoBCRequest,
  RespuestaApi,
  RotarPinOperadorFioriRequest,
  SolicitudPedidoBC,
  UmbralAlerta,
} from '@adelante/shared';
import { modoDev, msalInstance, obtenerIniciales, scopesApi, usuarioDev } from './auth';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Obtiene un access token para llamar a la API Adelante.
 *
 * - En modo dev: devuelve `null` (el backend skipea validación con AUTH_DEV_MODE=true).
 * - En modo real: intenta `acquireTokenSilent` primero (rápido, sin UI). Si
 *   falla por cualquier motivo de auth (token expirado, refresh token
 *   inválido, timeout del iframe silencioso) fallback automático a
 *   `acquireTokenPopup` que abre una ventana de Microsoft donde el usuario
 *   confirma sesión y vuelve. Esto resuelve el caso típico de
 *   `BrowserAuthError: timed_out` que vimos en producción cuando el iframe
 *   silencioso queda colgado o no puede renovar refresh token.
 */
// =============================================================================
// Sesión "Lab PIN" — alternativa al flujo MSAL para usuarios sin email
// =============================================================================
//
// Cuando el operario del laboratorio se loguea por PIN, el backend devuelve
// un JWT propio que guardamos en localStorage. Mientras esa key exista, la
// app actúa como un usuario "lab" sin tocar MSAL.
//
// - `auth_lab_token`: el JWT crudo (Bearer en Authorization).
// - `auth_lab_etiqueta`: nombre amigable para mostrar en el header.
// - `auth_lab_expira_en`: epoch ms cuando vence — si pasa, limpiamos y
//   forzamos re-login PIN.

const LAB_TOKEN_KEY = 'auth_lab_token';
const LAB_ETIQUETA_KEY = 'auth_lab_etiqueta';
const LAB_EXPIRA_KEY = 'auth_lab_expira_en';

export function leerTokenLab(): string | null {
  try {
    const token = localStorage.getItem(LAB_TOKEN_KEY);
    if (!token) return null;
    const expiraStr = localStorage.getItem(LAB_EXPIRA_KEY);
    const expira = expiraStr ? Number(expiraStr) : 0;
    if (expira && expira < Date.now()) {
      // Caducó — limpiar.
      eliminarSesionLab();
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function guardarSesionLab(token: string, etiqueta: string, expiraEn: number): void {
  localStorage.setItem(LAB_TOKEN_KEY, token);
  localStorage.setItem(LAB_ETIQUETA_KEY, etiqueta);
  localStorage.setItem(LAB_EXPIRA_KEY, String(expiraEn));
}

export function eliminarSesionLab(): void {
  localStorage.removeItem(LAB_TOKEN_KEY);
  localStorage.removeItem(LAB_ETIQUETA_KEY);
  localStorage.removeItem(LAB_EXPIRA_KEY);
}

export function esSesionLab(): boolean {
  return leerTokenLab() !== null;
}

export function obtenerEtiquetaLab(): string | null {
  return localStorage.getItem(LAB_ETIQUETA_KEY);
}

// =============================================================================
// Sesión "Fiori PIN" — igual que la sesión lab, pero cada operador tiene
// su propio PIN (login en 2 pasos: elige su nombre, tipea su PIN). El JWT
// resultante trae role=OperadorFiori y una identidad propia (así el
// backend puede acotar `/coladas` a lo que cada chofer registró).
// =============================================================================

const FIORI_TOKEN_KEY = 'auth_fiori_token';
const FIORI_NOMBRE_KEY = 'auth_fiori_nombre';
const FIORI_EXPIRA_KEY = 'auth_fiori_expira_en';

export function leerTokenFiori(): string | null {
  try {
    const token = localStorage.getItem(FIORI_TOKEN_KEY);
    if (!token) return null;
    const expiraStr = localStorage.getItem(FIORI_EXPIRA_KEY);
    const expira = expiraStr ? Number(expiraStr) : 0;
    if (expira && expira < Date.now()) {
      eliminarSesionFiori();
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

export function guardarSesionFiori(token: string, nombre: string, expiraEn: number): void {
  localStorage.setItem(FIORI_TOKEN_KEY, token);
  localStorage.setItem(FIORI_NOMBRE_KEY, nombre);
  localStorage.setItem(FIORI_EXPIRA_KEY, String(expiraEn));
}

export function eliminarSesionFiori(): void {
  localStorage.removeItem(FIORI_TOKEN_KEY);
  localStorage.removeItem(FIORI_NOMBRE_KEY);
  localStorage.removeItem(FIORI_EXPIRA_KEY);
}

export function esSesionFiori(): boolean {
  return leerTokenFiori() !== null;
}

export function obtenerNombreFiori(): string | null {
  return localStorage.getItem(FIORI_NOMBRE_KEY);
}

async function obtenerToken(): Promise<string | null> {
  if (modoDev) return null;

  // Modo lab PIN: si hay un token JWT propio en localStorage, lo usamos
  // en lugar de MSAL. La sesión lab dura ~30 días y se renueva con cada
  // login exitoso (el operario sólo re-tipea el PIN si el token caduca).
  const tokenLab = leerTokenLab();
  if (tokenLab) return tokenLab;

  const tokenFiori = leerTokenFiori();
  if (tokenFiori) return tokenFiori;

  if (!msalInstance) {
    throw new Error('MSAL no está inicializado');
  }

  const cuentas = msalInstance.getAllAccounts();
  const cuenta = cuentas[0];
  if (!cuenta) {
    // Sin sesión MSAL ni lab: devolvemos null para que `pedir()` haga
    // el request sin Authorization header. Útil para endpoints anónimos
    // (como /api/auth/lab/login) — el resto devuelven 401 que el caller
    // maneja como mensaje accionable, no como crash de auth.
    return null;
  }

  // redirectUri liviano SOLO para renovación silenciosa + popup. Ver
  // web/public/blank.html: evita que el iframe de renovación cargue toda la
  // app (causa de `block_iframe_reload` / `timed_out` → popup constante).
  // Debe estar registrado como redirect URI SPA en Entra ID.
  const redirectUriRenovacion = `${window.location.origin}/blank.html`;

  try {
    const respuesta = await msalInstance.acquireTokenSilent({
      scopes: scopesApi,
      account: cuenta,
      redirectUri: redirectUriRenovacion,
    });
    return respuesta.accessToken;
  } catch (e) {
    // Fallback interactivo. `acquireTokenPopup` abre una ventana chiquita
    // de Microsoft, el usuario confirma (o entra la contraseña si caducó),
    // y devuelve el token sin perder el estado del SPA. Con el blank.html
    // debería ser raro llegar acá (solo cuando el refresh token realmente
    // caducó o Conditional Access exige MFA).
    //
    // Si el popup también falla (usuario cancela o popup bloqueado), dejamos
    // que el error suba — el caller lo muestra como toast.
    console.warn('acquireTokenSilent falló, intentando popup:', e);
    const respuesta = await msalInstance.acquireTokenPopup({
      scopes: scopesApi,
      account: cuenta,
      redirectUri: redirectUriRenovacion,
    });
    return respuesta.accessToken;
  }
}

/**
 * Wrapper genérico sobre `fetch` que añade auth, content-type y maneja errores
 * HTTP a alto nivel. Las funciones del cliente lo usan internamente.
 */
async function pedir<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  const token = await obtenerToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((opciones?.headers ?? {}) as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const respuesta = await fetch(`${API_URL}${ruta}`, { ...opciones, headers });

  if (!respuesta.ok) {
    // Si el backend devolvió RespuestaApi<never> con `error.mensaje`, lo
    // propagamos para que los toasts y dialogs puedan mostrar mensajes
    // accionables (típicamente 409 CONFLICTO_ESTADO en mutaciones de coladas).
    let mensaje = `HTTP ${respuesta.status}`;
    let codigo: string | undefined;
    let detalles: unknown;
    try {
      const body = (await respuesta.json()) as {
        error?: { mensaje?: string; codigo?: string; detalles?: unknown };
      };
      if (body?.error?.mensaje) {
        mensaje = body.error.mensaje;
        codigo = body.error.codigo;
        detalles = body.error.detalles;
        if (detalles !== undefined) {
          console.error(`[API ${respuesta.status}] ${mensaje}`, detalles);
        }
      }
    } catch {
      // body no era JSON o estaba vacío; queda el status genérico.
    }
    throw new ApiError(respuesta.status, codigo, mensaje, detalles);
  }

  return (await respuesta.json()) as T;
}

/**
 * Error enriquecido que devuelve `pedir()` ante respuestas non-2xx.
 * Las UIs lo pueden inspectionar para mostrar dialogs personalizados
 * por código (ej: 409 ESTADO_INVALIDO en consolidación). Si el caller
 * solo necesita el texto, usar `.message` igual que cualquier Error.
 */
export class ApiError extends Error {
  status: number;
  codigo: string | undefined;
  detalles: unknown;
  constructor(status: number, codigo: string | undefined, mensaje: string, detalles?: unknown) {
    super(mensaje);
    this.name = 'ApiError';
    this.status = status;
    this.codigo = codigo;
    this.detalles = detalles;
  }
}

function construirQueryString(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Cliente API tipado. Cada agrupación corresponde a un módulo del backend.
 * Para agregar un endpoint nuevo: extender este objeto y reutilizar `pedir<T>`.
 */
export const cliente = {
  plantas: {
    listar: () => pedir<RespuestaApi<ListaPlantasResponse>>('/api/plantas'),
  },
  batches: {
    obtenerKpis: (params: KpisRequest) => {
      const qs = construirQueryString({
        desde: params.desde,
        hasta: params.hasta,
        id_planta: params.id_planta,
      });
      return pedir<RespuestaApi<KpisResponse>>(`/api/batches/kpis${qs}`);
    },
    ingestar: (body: IngestaBlendRequest) =>
      pedir<RespuestaApi<IngestaBlendResponse>>('/api/batches/ingestar', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    huerfanos: () =>
      pedir<RespuestaApi<{ huerfanos: BatchHuerfano[]; total: number }>>('/api/batches/huerfanos'),
    listar: (params: Partial<ListarBatchesRequest> = {}) => {
      const qs = construirQueryString({
        id_colada: params.id_colada,
        id_planta: params.id_planta,
        desde: params.desde,
        hasta: params.hasta,
        q: params.q,
        solo_anomalias: params.solo_anomalias ? 'true' : undefined,
        pagina: params.pagina,
        por_pagina: params.por_pagina,
      });
      return pedir<RespuestaApi<ListaBatchesResponse>>(`/api/batches${qs}`);
    },
  },
  densidades: {
    listar: () => pedir<RespuestaApi<ListaDensidadesResponse>>('/api/densidades-materiales'),
    crear: (body: CrearDensidadRequest) =>
      pedir<RespuestaApi<DensidadMaterial>>('/api/densidades-materiales', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizar: (clave: string, body: ActualizarDensidadRequest) =>
      pedir<RespuestaApi<DensidadMaterial>>(
        `/api/densidades-materiales/${encodeURIComponent(clave)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
  },
  umbrales: {
    listar: () => pedir<RespuestaApi<ListaUmbralesResponse>>('/api/umbrales-alerta'),
    actualizar: (clave: string, body: ActualizarUmbralRequest) =>
      pedir<RespuestaApi<UmbralAlerta>>(`/api/umbrales-alerta/${encodeURIComponent(clave)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  mapeoRecetas: {
    listar: () => pedir<RespuestaApi<ListaRecetasBlendMapeoResponse>>('/api/mapeo-recetas'),
    guardar: (body: GuardarMapeoRecetaRequest) =>
      pedir<RespuestaApi<RecetaBlendMapeo>>('/api/mapeo-recetas', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  bcCatalogo: {
    productosEnsamblados: () =>
      pedir<RespuestaApi<ListaProductosEnsambladosBCResponse>>('/api/bc/productos-ensamblados'),
    bom: (codigo: string) =>
      pedir<RespuestaApi<ListaBomBCResponse>>(`/api/bc/bom/${encodeURIComponent(codigo)}`),
  },
  importaciones: {
    listar: (params: HistorialImportacionesRequest) => {
      const qs = construirQueryString({ limite: params.limite, offset: params.offset });
      return pedir<RespuestaApi<HistorialImportacionesResponse>>(`/api/importaciones${qs}`);
    },
  },
  coladas: {
    listar: (params: Partial<ListarColadasRequest> = {}) => {
      // `estado`, `id_planta` y `pedido_bc_estado` son arrays → se
      // serializan como parámetro repetido.
      const usp = new URLSearchParams();
      if (params.desde) usp.set('desde', params.desde);
      if (params.hasta) usp.set('hasta', params.hasta);
      if (params.q) usp.set('q', params.q);
      if (params.receta_blend_q) usp.set('receta_blend_q', params.receta_blend_q);
      if (params.pedido_bc_q) usp.set('pedido_bc_q', params.pedido_bc_q);
      if (params.orden) usp.set('orden', params.orden);
      if (params.pagina) usp.set('pagina', String(params.pagina));
      if (params.por_pagina) usp.set('por_pagina', String(params.por_pagina));
      for (const e of params.estado ?? []) usp.append('estado', e);
      for (const id of params.id_planta ?? []) usp.append('id_planta', String(id));
      for (const e of params.pedido_bc_estado ?? []) usp.append('pedido_bc_estado', e);
      const qs = usp.toString();
      return pedir<
        RespuestaApi<{
          coladas: ColadaListadoItem[];
          total: number;
          pagina: number;
          por_pagina: number;
        }>
      >(`/api/coladas${qs ? `?${qs}` : ''}`);
    },
    obtener: (id: number) => pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}`),
    /** Registro manual (autocargables Fiori — sin dosificador ni CSV). */
    crearManual: (body: CrearColadaManualRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>('/api/coladas/manual', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarM3Manual: (id: number, body: ActualizarM3ColadaManualRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/m3-manual`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    obtenerBom: (id: number) =>
      pedir<RespuestaApi<ObtenerBomColadaResponse>>(`/api/coladas/${id}/bom`),
    guardarBomOverride: (id: number, codigo: string, body: GuardarBomOverrideRequest) =>
      pedir<RespuestaApi<ObtenerBomColadaResponse>>(
        `/api/coladas/${id}/bom/${encodeURIComponent(codigo)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    confirmar: (id: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/confirmar`, { method: 'POST' }),
    desconfirmar: (id: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/desconfirmar`, { method: 'POST' }),
    marcarDigitada: (id: number, body: MarcarDigitadaRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/marcar-digitada`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    desmarcarDigitada: (id: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/desmarcar-digitada`, {
        method: 'POST',
      }),
    cerrar: (id: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/cerrar`, { method: 'POST' }),
    anular: (id: number, body: AnularColadaRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/anular`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    desanular: (id: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/desanular`, { method: 'POST' }),
    excluirBatch: (idColada: number, idBatch: number, body: ExcluirBatchRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${idColada}/excluir-batch/${idBatch}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    agregarBatch: (idColadaDestino: number, idBatch: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(
        `/api/coladas/${idColadaDestino}/agregar-batch/${idBatch}`,
        { method: 'POST' },
      ),
    restaurarBatch: (idColada: number, idBatch: number) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${idColada}/restaurar-batch/${idBatch}`, {
        method: 'POST',
      }),
    asignarObra: (id: number, body: AsignarObraRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>(`/api/coladas/${id}/asignar-obra`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    consolidar: (body: ConsolidarColadasRequest) =>
      pedir<RespuestaApi<ColadaDetalle>>('/api/coladas/consolidar', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    anularBulk: (body: AnularColadasBulkRequest) =>
      pedir<RespuestaApi<AnularColadasBulkResponse>>('/api/coladas/anular-bulk', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    previewPedidoBC: (id: number) =>
      pedir<RespuestaApi<PreviewPedidoEnsambladoBC>>(`/api/coladas/${id}/pedido-bc/preview`),
    /**
     * Ya NO crea el pedido en BC directo — crea una SOLICITUD pendiente de
     * autorización (ver `pedidosBc.aprobar`/`rechazar`).
     */
    solicitarPedidoBC: (id: number, body: CrearPedidoEnsambladoBCRequest) =>
      pedir<RespuestaApi<SolicitudPedidoBC>>(`/api/coladas/${id}/pedido-bc`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  pedidosBc: {
    listar: (estado?: EstadoSolicitudPedidoBC) => {
      const qs = estado ? `?estado=${estado}` : '';
      return pedir<RespuestaApi<ListaSolicitudesPedidoBCResponse>>(
        `/api/pedidos-bc-solicitudes${qs}`,
      );
    },
    aprobar: (id: number) =>
      pedir<RespuestaApi<SolicitudPedidoBC>>(`/api/pedidos-bc-solicitudes/${id}/aprobar`, {
        method: 'POST',
      }),
    rechazar: (id: number, body: RechazarSolicitudPedidoBCRequest) =>
      pedir<RespuestaApi<SolicitudPedidoBC>>(`/api/pedidos-bc-solicitudes/${id}/rechazar`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  diagnosticoBC: {
    config: () => pedir<RespuestaApi<unknown>>('/api/diagnostico/bc/config'),
    token: () => pedir<RespuestaApi<unknown>>('/api/diagnostico/bc/token'),
    companies: () => pedir<RespuestaApi<unknown>>('/api/diagnostico/bc/companies'),
    assembly: () => pedir<RespuestaApi<unknown>>('/api/diagnostico/bc/assembly'),
    items: () => pedir<RespuestaApi<unknown>>('/api/diagnostico/bc/items'),
    bom: (codigo: string) =>
      pedir<RespuestaApi<unknown>>(`/api/diagnostico/bc/bom/${encodeURIComponent(codigo)}`),
  },
  obras: {
    listar: (params: Partial<ListarObrasRequest> = {}) => {
      const qs = construirQueryString({
        q: params.q,
        solo_activas: params.solo_activas === undefined ? undefined : String(params.solo_activas),
        limite: params.limite,
      });
      return pedir<RespuestaApi<{ obras: Obra[]; total: number }>>(`/api/obras${qs}`);
    },
  },
  lab: {
    listarActividades: (incluirInactivas = false) =>
      pedir<RespuestaApi<{ actividades: Actividad[] }>>(
        `/api/lab/actividades${incluirInactivas ? '?incluye_inactivas=true' : ''}`,
      ),
    listarCurvaTeorica: () =>
      pedir<RespuestaApi<{ puntos: PuntoCurvaTeorica[] }>>('/api/lab/curva-teorica'),
    importarExcel: async (archivo: File): Promise<RespuestaApi<ImportarExcelLabResponse>> => {
      // Body crudo binario (no multipart). Más simple para un único archivo.
      const ab = await archivo.arrayBuffer();
      return pedir<RespuestaApi<ImportarExcelLabResponse>>('/api/lab/importar-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: ab,
      });
    },
    crearActividad: (body: CrearActividadRequest) =>
      pedir<RespuestaApi<Actividad>>('/api/lab/actividades', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarActividad: (
      id: number,
      body: { nombre?: string; orden?: number; activo?: boolean },
    ) =>
      pedir<RespuestaApi<Actividad>>(`/api/lab/actividades/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    listarMuestras: (params: Partial<ListarMuestrasRequest> = {}) => {
      const qs = construirQueryString({
        obra_works_no: params.obra_works_no,
        id_actividad: params.id_actividad,
        fc_objetivo: params.fc_objetivo,
        desde: params.desde,
        hasta: params.hasta,
        q: params.q,
        cumplimiento: params.cumplimiento,
        pagina: params.pagina,
        por_pagina: params.por_pagina,
      });
      return pedir<RespuestaApi<ListaMuestrasResponse>>(`/api/lab/muestras${qs}`);
    },
    obtenerMuestra: (id: number) => pedir<RespuestaApi<MuestraDetalle>>(`/api/lab/muestras/${id}`),
    crearMuestra: (body: CrearMuestraRequest) =>
      pedir<RespuestaApi<MuestraDetalle>>('/api/lab/muestras', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarMuestra: (id: number, body: ActualizarMuestraRequest) =>
      pedir<RespuestaApi<MuestraDetalle>>(`/api/lab/muestras/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    eliminarMuestra: (id: number) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/muestras/${id}`, { method: 'DELETE' }),
    crearEnsayo: (idMuestra: number, body: CrearEnsayoRequest) =>
      pedir<RespuestaApi<Ensayo>>(`/api/lab/muestras/${idMuestra}/ensayos`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarEnsayo: (id: number, body: ActualizarEnsayoRequest) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/ensayos/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    eliminarEnsayo: (id: number) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/ensayos/${id}`, { method: 'DELETE' }),
    crearMedicion: (idEnsayo: number, body: CrearMedicionRequest) =>
      pedir<RespuestaApi<Medicion>>(`/api/lab/ensayos/${idEnsayo}/mediciones`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarMedicion: (id: number, body: ActualizarMedicionRequest) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/mediciones/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    eliminarMedicion: (id: number) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/mediciones/${id}`, { method: 'DELETE' }),
    // --- Fotos de muestra (Azure Blob) ---
    listarFotos: (idMuestra: number) =>
      pedir<RespuestaApi<ListaFotosMuestraResponse>>(`/api/lab/muestras/${idMuestra}/fotos`),
    subirFoto: (
      idMuestra: number,
      blob: Blob,
      opts?: { nombre?: string; idEnsayo?: number | null },
    ) => {
      const params = new URLSearchParams();
      if (opts?.nombre) params.set('nombre', opts.nombre);
      if (opts?.idEnsayo != null) params.set('id_ensayo', String(opts.idEnsayo));
      const qs = params.toString() ? `?${params.toString()}` : '';
      return pedir<RespuestaApi<FotoMuestra>>(`/api/lab/muestras/${idMuestra}/fotos${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': blob.type || 'image/jpeg' },
        body: blob,
      });
    },
    actualizarFoto: (id: number, body: { id_ensayo: number | null }) =>
      pedir<RespuestaApi<FotoMuestra>>(`/api/lab/fotos/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    eliminarFoto: (id: number) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/fotos/${id}`, { method: 'DELETE' }),

    // --- Esclerómetro (ensayo no destructivo) ---
    listarEsclerometro: (params: Partial<ListarEnsayosEsclerometroRequest> = {}) => {
      const qs = construirQueryString({
        obra_works_no: params.obra_works_no,
        desde: params.desde,
        hasta: params.hasta,
        q: params.q,
        pagina: params.pagina,
        por_pagina: params.por_pagina,
      });
      return pedir<RespuestaApi<ListaEnsayosEsclerometroResponse>>(`/api/lab/esclerometro${qs}`);
    },
    obtenerEsclerometro: (id: number) =>
      pedir<RespuestaApi<EnsayoEsclerometroDetalle>>(`/api/lab/esclerometro/${id}`),
    crearEsclerometro: (body: CrearEnsayoEsclerometroRequest) =>
      pedir<RespuestaApi<EnsayoEsclerometroDetalle>>('/api/lab/esclerometro', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarEsclerometro: (id: number, body: ActualizarEnsayoEsclerometroRequest) =>
      pedir<RespuestaApi<EnsayoEsclerometroDetalle>>(`/api/lab/esclerometro/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    eliminarEsclerometro: (id: number) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/esclerometro/${id}`, { method: 'DELETE' }),
    crearRebote: (idEnsayo: number, body: CrearReboteRequest) =>
      pedir<RespuestaApi<Rebote>>(`/api/lab/esclerometro/${idEnsayo}/rebotes`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    actualizarRebote: (id: number, body: ActualizarReboteRequest) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/esclerometro/rebotes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    eliminarRebote: (id: number) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/lab/esclerometro/rebotes/${id}`, {
        method: 'DELETE',
      }),
  },
  usuarios: {
    listar: (params: Partial<ListarUsuariosRequest> = {}) => {
      const qs = construirQueryString({
        q: params.q,
        limite: params.limite,
      });
      return pedir<RespuestaApi<ListaUsuariosResponse>>(`/api/usuarios${qs}`);
    },
    asignarRol: (userId: string, body: AsignarRolRequest) =>
      pedir<RespuestaApi<{ assignmentId: string; yaExistia: boolean }>>(
        `/api/usuarios/${userId}/asignar-rol`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    quitarRol: (userId: string, assignmentId: string) =>
      pedir<RespuestaApi<{ ok: true }>>(`/api/usuarios/${userId}/asignaciones/${assignmentId}`, {
        method: 'DELETE',
      }),
  },
  authLab: {
    /** Login por PIN — anónimo, no requiere token previo. */
    login: (body: LoginLabPinRequest) =>
      pedir<RespuestaApi<LoginLabPinResponse>>('/api/auth/lab/login', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  adminLabPin: {
    /** Estado del PIN — Admin only. No expone el PIN ni el hash. */
    estado: () => pedir<RespuestaApi<EstadoPinLabResponse>>('/api/lab-pin/estado'),
    /** Configurar / rotar PIN — Admin only. */
    configurar: (body: ConfigurarPinLabRequest) =>
      pedir<RespuestaApi<EstadoPinLabResponse>>('/api/lab-pin', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },
  authFiori: {
    /** Lista pública de operadores activos — para el selector de `/fiori-login`. */
    operadoresPublicos: () =>
      pedir<RespuestaApi<ListaOperadoresFioriPublicosResponse>>('/api/fiori-operadores/publicos'),
    /** Login por id_operador + PIN — anónimo, no requiere token previo. */
    login: (body: LoginFioriPinRequest) =>
      pedir<RespuestaApi<LoginFioriPinResponse>>('/api/auth/fiori/login', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
  fioriOperadores: {
    /** Lista completa (incluye inactivos) — Admin only. */
    listar: () => pedir<RespuestaApi<ListaOperadoresFioriAdminResponse>>('/api/fiori-operadores'),
    /** Da de alta un nuevo operador con su PIN — Admin only. */
    crear: (body: CrearOperadorFioriRequest) =>
      pedir<RespuestaApi<OperadorFioriAdmin>>('/api/fiori-operadores', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** Rota el PIN de un operador existente — Admin only. */
    rotarPin: (id: number, body: RotarPinOperadorFioriRequest) =>
      pedir<RespuestaApi<OperadorFioriAdmin>>(`/api/fiori-operadores/${id}/pin`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    /** Activa/desactiva un operador — Admin only. */
    cambiarActivo: (id: number, body: CambiarActivoOperadorFioriRequest) =>
      pedir<RespuestaApi<OperadorFioriAdmin>>(`/api/fiori-operadores/${id}/activo`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },
};

/** Información del usuario activo (modo dev o MSAL real). */
export interface UsuarioActivo {
  name: string;
  username: string;
  iniciales: string;
  /** Roles del usuario en la App Registration (claim `roles` del idToken). */
  roles: string[];
}

/**
 * Decodifica el payload de un JWT (base64url) sin validar firma. Solo lo
 * usamos del lado cliente para leer claims (auth real la hace el backend
 * con `jose` validando firma + issuer + audience).
 */
function decodificarJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/**
 * Lee el claim `roles` directamente del idToken cacheado por MSAL.
 * Esto es más confiable que `cuenta.idTokenClaims.roles` — MSAL no
 * siempre expone los claims en el objeto de la cuenta después de un
 * re-login, pero el JWT sí está guardado.
 *
 * Buscamos en localStorage primero (configuración actual) y caemos a
 * sessionStorage como fallback (sesiones viejas previas al cambio).
 */
function leerRolesDesdeSessionStorage(homeAccountId: string): string[] {
  try {
    const buscar = (store: Storage): string | null => {
      const key = Object.keys(store).find(
        (k) => k.includes('idtoken') && k.includes(homeAccountId),
      );
      return key ? store.getItem(key) : null;
    };
    const raw = buscar(localStorage) ?? buscar(sessionStorage);
    if (!raw) return [];
    // MSAL guarda el idToken en un JSON con campo `secret` (el JWT raw).
    let jwt = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && typeof parsed.secret === 'string') {
        jwt = parsed.secret;
      }
    } catch {
      // Si raw no es JSON, lo tratamos como el JWT directo.
    }
    const payload = decodificarJwtPayload(jwt);
    const roles = (payload as { roles?: unknown } | null)?.roles;
    return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Obtiene el usuario activo. En modo dev devuelve `usuarioDev`.
 * En modo MSAL devuelve la primera cuenta o `null` si no hay sesión.
 */
export function obtenerUsuarioActual(): UsuarioActivo | null {
  if (modoDev) {
    // En dev otorgamos todos los roles así no hace falta gestionar Entra ID
    // para probar la pantalla admin.
    return { ...usuarioDev, roles: ['Admin', 'Operador', 'Laboratorio'] };
  }

  // Sesión por PIN (lab): construimos el "usuario activo" desde
  // localStorage. No hay cuenta MSAL en este modo.
  if (esSesionLab()) {
    const etiqueta = obtenerEtiquetaLab() ?? 'Laboratorio';
    return {
      name: etiqueta,
      username: 'lab',
      iniciales: 'LB',
      roles: ['Laboratorio'],
    };
  }

  // Sesión por PIN (Fiori): cada operador tiene su propio nombre, a
  // diferencia de la sesión lab (etiqueta única compartida).
  if (esSesionFiori()) {
    const nombre = obtenerNombreFiori() ?? 'Operador Fiori';
    return {
      name: nombre,
      username: 'fiori',
      iniciales: obtenerIniciales(nombre),
      roles: ['OperadorFiori'],
    };
  }

  const cuenta = msalInstance?.getAllAccounts()[0];
  if (!cuenta) return null;

  const nombre = cuenta.name ?? cuenta.username;
  // Preferimos idTokenClaims.roles (lo "oficial" de MSAL), con fallback a
  // decodificar el JWT del sessionStorage. Vimos casos en producción donde
  // MSAL no popula idTokenClaims después de un re-login en InPrivate.
  const claims = cuenta.idTokenClaims as { roles?: string[] } | undefined;
  let roles = Array.isArray(claims?.roles)
    ? claims.roles.filter((r): r is string => typeof r === 'string')
    : [];
  if (roles.length === 0 && cuenta.homeAccountId) {
    roles = leerRolesDesdeSessionStorage(cuenta.homeAccountId);
  }

  return {
    name: nombre,
    username: cuenta.username,
    iniciales: obtenerIniciales(nombre),
    roles,
  };
}

/** Atajo: ¿el usuario activo tiene rol Admin? Útil para mostrar/ocultar UI admin. */
export function esAdmin(): boolean {
  const u = obtenerUsuarioActual();
  return u?.roles.includes('Admin') ?? false;
}

/**
 * Cierra la sesión limpiando todo el cache de MSAL y redirigiendo al login.
 * Útil cuando el token quedó corrupto / atascado en `timed_out` repetidos.
 *
 * Usa `logoutRedirect` para que Microsoft también limpie su side y al
 * volver no haya estado residual. En dev (sin MSAL) recarga la página.
 */
export async function cerrarSesion(): Promise<void> {
  // Si era sesión lab (PIN), simplemente limpiamos y mandamos al login PIN.
  if (esSesionLab()) {
    eliminarSesionLab();
    window.location.href = `${window.location.origin}/lab-login`;
    return;
  }

  // Igual que lab, pero para operadores Fiori.
  if (esSesionFiori()) {
    eliminarSesionFiori();
    window.location.href = `${window.location.origin}/fiori-login`;
    return;
  }

  if (modoDev || !msalInstance) {
    window.location.reload();
    return;
  }
  // Limpiamos cualquier cache local primero (por si el redirect falla).
  try {
    const cuenta = msalInstance.getAllAccounts()[0];
    await msalInstance.logoutRedirect({
      account: cuenta,
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (e) {
    // Si el redirect tira (popup blockers, etc), borramos manualmente
    // los keys de MSAL y forzamos un reload — eso siempre funciona.
    console.warn('logoutRedirect falló, limpiando cache manual:', e);
    for (const store of [localStorage, sessionStorage]) {
      const keys = Object.keys(store).filter((k) => k.startsWith('msal.'));
      for (const k of keys) store.removeItem(k);
    }
    window.location.href = window.location.origin;
  }
}

/**
 * ¿El usuario activo es "solo Laboratorio"? Es decir, tiene el rol
 * Laboratorio pero ninguno de los roles operativos (Admin, Operador,
 * Ingeniería). Usado para esconder la nav que no le sirve y redirigirlo
 * directo a `/lab/muestras` al loguearse.
 *
 * Los usuarios de lab son muy básicos: solo necesitan ver Laboratorio.
 */
export function esSoloLab(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  const tieneLab = u.roles.includes('Laboratorio');
  if (!tieneLab) return false;
  const tieneOtros =
    u.roles.includes('Admin') || u.roles.includes('Operador') || u.roles.includes('Ingenieria');
  return !tieneOtros;
}

/**
 * ¿El usuario activo puede modificar la configuración (umbrales, densidades)
 * y autorizar (aprobar/rechazar) envíos a BC? Admin e Ingeniería. Coincide
 * con ROLES_CONFIG del API.
 */
export function puedeConfigurar(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  return u.roles.includes('Admin') || u.roles.includes('Ingenieria');
}

/**
 * ¿El usuario activo puede operar coladas día a día (confirmar/digitar/
 * cerrar/asignar obra/consolidar/solicitar envío a BC)? Admin y Operador
 * únicamente — coincide con ROLES_OPERATIVOS del API. Ingeniería quedó
 * deliberadamente afuera: es quien autoriza esos envíos (`puedeConfigurar`),
 * no quien los solicita.
 */
export function puedeOperarColadas(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  return u.roles.includes('Admin') || u.roles.includes('Operador');
}

/**
 * ¿El usuario activo puede registrar coladas Fiori (`/coladas/registrar-fiori`)?
 * Admin, Operador y OperadorFiori — este último es el rol acotado pensado
 * para los choferes de las autocargables: solo registran su colada y
 * revisan las que ELLOS registraron (el backend acota `/coladas` por
 * `creada_por_oid` cuando el usuario no tiene ningún otro rol — ver
 * `esSoloOperadorFiori`).
 */
export function puedeRegistrarFiori(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  return (
    u.roles.includes('Admin') || u.roles.includes('Operador') || u.roles.includes('OperadorFiori')
  );
}

/**
 * ¿El usuario activo tiene SOLO el rol OperadorFiori (ningún rol con
 * visibilidad amplia: Admin/Operador/Ingeniería/Laboratorio)? Igual que
 * `esSoloLab()`, pero para el chofer de Fiori — simplifica el header a
 * "Registrar" + "Mis Registros" y lo aterriza ahí en vez del dashboard.
 */
export function esSoloOperadorFiori(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  const tieneRol = u.roles.includes('OperadorFiori');
  if (!tieneRol) return false;
  const tieneOtros =
    u.roles.includes('Admin') ||
    u.roles.includes('Operador') ||
    u.roles.includes('Ingenieria') ||
    u.roles.includes('Laboratorio');
  return !tieneOtros;
}

/**
 * ¿El usuario activo puede mapear Receta Blend → Producto BC? Admin,
 * Ingeniería y Operador — coincide con ROLES_MAPEO_RECETAS del API. A
 * diferencia de `puedeConfigurar` (autorizar envíos a BC, umbrales,
 * densidades), acá sí entra Operador: es quien registra las coladas Fiori
 * y puede toparse con un "tipo de concreto" sin homologar todavía.
 */
export function puedeMapearRecetas(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  return (
    u.roles.includes('Admin') || u.roles.includes('Ingenieria') || u.roles.includes('Operador')
  );
}

/**
 * ¿El usuario activo puede ver/escribir en el módulo Laboratorio?
 * Admin tiene acceso a todo. Laboratorio es el rol dedicado. Ingeniería
 * combina Operador + Laboratorio, así que también accede.
 */
export function puedeLab(): boolean {
  const u = obtenerUsuarioActual();
  if (!u) return false;
  return (
    u.roles.includes('Admin') || u.roles.includes('Laboratorio') || u.roles.includes('Ingenieria')
  );
}

/**
 * ¿El usuario activo tiene permisos de escritura en Laboratorio?
 * Mismo set que `puedeLab` por ahora — Operador no escribe Lab.
 */
export function puedeEscribirLab(): boolean {
  return puedeLab();
}
